import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Sparkles, Tv, TrendingUp, Check } from 'lucide-react';

const components = [
  {
    icon: <Sparkles className="w-7 h-7" />,
    title: 'Platform Fee — from $500/mo',
    desc: 'A flat monthly fee that covers AI video creative generation, campaign management, and your white-label dashboard and reporting. This is where the AI creative lives — broadcast-ready spots without a production team.'
  },
  {
    icon: <Tv className="w-7 h-7" />,
    title: 'Media Budget — set by your client',
    desc: 'Your client\'s streaming ad spend, billed on impressions (CPM) across premium CTV inventory. You decide the budget with your client — there is no minimum imposed by us on top of the platform fee.'
  },
  {
    icon: <TrendingUp className="w-7 h-7" />,
    title: 'Your Commission — on top',
    desc: 'You mark up media at 25–40%. That margin is yours to keep — a new recurring revenue stream that scales with your client roster, with no headcount added.'
  }
];

const CastPricingPage = () => {
  const [budget, setBudget] = useState(2000);
  const [markup, setMarkup] = useState(30);
  const margin = Math.round(budget * (markup / 100));

  return (
    <div className="min-h-screen py-20" data-testid="cast-pricing-page">
      <div className="container">
        {/* Hero */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="cast-pricing-hero-title">
            How MarqiCast Pricing Works.
          </h1>
          <p className="body-lg mb-4">
            CTV pricing isn't one flat number — it's a platform fee plus media. We keep it transparent so
            you can calculate your margin before you ever book a call.
          </p>
        </div>

        {/* Three components */}
        <section className="mb-24">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {components.map((c) => (
              <div key={c.title} className="feature-card" data-testid={`cast-pricing-component-${c.title.split(' ')[0].toLowerCase()}`}>
                <div className="w-14 h-14 rounded-2xl bg-[var(--accent-bg)] flex items-center justify-center text-[var(--accent-primary)] mb-6">
                  {c.icon}
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-3">{c.title}</h3>
                <p className="body-md">{c.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Margin calculator */}
        <section className="mb-24 bg-[var(--bg-secondary)] -mx-6 px-6 py-16 rounded-2xl">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="h1 text-[var(--text-primary)] mb-6">Calculate Your Margin.</h2>
            <p className="body-lg">
              Drag the sliders to see what a single CTV client could add to your agency's monthly revenue —
              before your management fee.
            </p>
          </div>

          <div className="max-w-2xl mx-auto grid gap-10">
            <div>
              <div className="flex justify-between mb-3">
                <label className="text-[var(--text-secondary)]">Client monthly media budget</label>
                <span className="font-semibold text-[var(--accent-primary)]">${budget.toLocaleString()}</span>
              </div>
              <input
                type="range" min="500" max="20000" step="500" value={budget}
                onChange={(e) => setBudget(Number(e.target.value))}
                className="w-full accent-[var(--accent-primary)]"
                data-testid="cast-pricing-budget-slider"
              />
            </div>
            <div>
              <div className="flex justify-between mb-3">
                <label className="text-[var(--text-secondary)]">Your media markup</label>
                <span className="font-semibold text-[var(--accent-primary)]">{markup}%</span>
              </div>
              <input
                type="range" min="25" max="40" step="1" value={markup}
                onChange={(e) => setMarkup(Number(e.target.value))}
                className="w-full accent-[var(--accent-primary)]"
                data-testid="cast-pricing-markup-slider"
              />
            </div>

            <div className="feature-card text-center">
              <p className="body-md mb-2">Your margin from this one client</p>
              <div className="display-md text-[var(--accent-primary)]" data-testid="cast-pricing-margin-result">
                ${margin.toLocaleString()}<span className="text-lg text-[var(--text-muted)]">/mo</span>
              </div>
              <p className="text-sm text-[var(--text-muted)] mt-3">
                Example: a ${budget.toLocaleString()}/mo client media budget marked up {markup}% = ${margin.toLocaleString()} margin per month —
                before your management fee, on top of the $500+ platform fee.
              </p>
            </div>
          </div>
        </section>

        {/* What's included */}
        <section className="mb-24 max-w-3xl mx-auto">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-10">What the Platform Fee Covers.</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'AI-generated broadcast-ready video creative',
              'Premium CTV inventory access & media buying',
              'Hyperlocal audience targeting & setup',
              'White-label dashboard & monthly reporting',
              'Campaign management & optimisation',
              'Measurement — foot traffic, web attribution, ROAS'
            ].map((item) => (
              <div key={item} className="flex items-start space-x-3 p-4 bg-[var(--bg-secondary)] rounded-lg">
                <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-secondary)]">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="feature-card max-w-4xl mx-auto" style={{ background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--accent-bg) 100%)' }}>
            <h2 className="h1 text-[var(--text-primary)] mb-6">Get a Custom Quote in Your Session.</h2>
            <p className="body-lg mb-12 max-w-3xl mx-auto">
              Every agency's client mix is different. Book a strategy session and we'll build a plan around
              your clients' budgets — and generate a sample AI spot for one of them, free.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect" data-testid="cast-pricing-final-cta">
                Book a Strategy Session — Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/cast" className="btn-secondary">Back to MarqiCast</Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CastPricingPage;
